// components/TestRunner/rules/Rule04_FiberCategory_Exclusivity.js
import { RuleUtils } from "../../../utils/RuleUtils.js";

/**
 * Rule04_FiberCategory_Exclusivity.js
 *
 * ✅ Règle 04 — Exclusivité des catégories de fibre [Global]
 * Type: Error
 *
 * 📌 Libellé: Une fibre doit appartenir à une seule catégorie : Réussite, Échec,
 * Hors Service ou Occupée. Interdiction d'appartenir à plusieurs.
 *
 * 📋 Contrôles effectués:
 *    • Vérifier qu'une fibre libre n'a qu'une seule catégorie PV active
 *    • Contrôler l'exclusivité entre Réussite, Échec et Hors Service
 *    • S'applique uniquement aux fibres libres
 *
 * 🛠️ Actions requises:
 *    • Déterminer la catégorie correcte de la fibre
 *    • Supprimer les catégories PV en conflit
 *    • Vérifier la cohérence des données
 */

export class Rule04_FiberCategory_Exclusivity {
  /**
   * Appliquer la règle 04 : Exclusivité des catégories
   * @param {Object} fiber - Objet fibre avec toutes ses données
   */
  static apply(fiber) {
    // On ne teste que les fibres libres
    if (fiber.etat !== "libre") {
      return; // Pas de test pour les fibres occupées
    }

    // Compter combien de catégories PV sont actives
    const categories = [];

    if (this._isReussiteCategory(fiber)) categories.push("Réussite");
    if (this._isEchecCategory(fiber)) categories.push("Échec");
    if (this._isHsCategory(fiber)) categories.push("Hors Service");

    if (categories.length > 1) {
      this._addError(
        fiber,
        "R04",
        "Conflit de catégories",
        `La fibre ${RuleUtils.formatFiberId(fiber.id)} appartient à plusieurs catégories simultanément.<br><br>` +
          `<strong>ÉTAT ACTUEL:</strong><br>` +
          `- État: ${fiber.etat || "Non défini"}<br>` +
          `- Catégories actives: ${categories.join(", ")}<br>` +
          `<strong>DÉTAILS DES FLAGS:</strong><br>` +
          `- PV de Réussite dans le DCO: ${fiber.hasDcoPvReussite ? "✅" : "❌"}<br>` +
          `- Fichiers SOR pour un PV Réussite: ${fiber.hasPvReussite ? "✅" : "❌"}<br>` +
          `- PV d'Échec dans le DCO: ${fiber.hasDcoPvEchec ? "✅" : "❌"}<br>` +
          `- Fichiers SOR pour un PV d'Échec SOR: ${fiber.hasPvEchec ? "✅" : "❌"}<br>` +
          `- PV Hors Service: ${fiber.hasPvHs ? "✅" : "❌"}<br>` +
          `<strong>⚠️ PROBLÈME DÉTECTÉ:</strong><br>` +
          `- Une fibre ne peut appartenir qu'à une seule catégorie<br>` +
          `<strong>⚡ ACTION REQUISE:</strong><br>` +
          `- Déterminer la catégorie correcte de la fibre<br>` +
          `- Supprimer les catégories PV en conflit<br>` +
          `- Vérifier la cohérence des données sur le terrain<br>` +
          `- Conserver uniquement la catégorie qui correspond à l'état réel`
      );
    }
  }

  /**
   * Vérifier si une fibre est dans la catégorie Réussite
   * @param {Object} fiber - Objet fibre
   * @returns {boolean} true si la fibre est en catégorie Réussite uniquement
   */
  static _isReussiteCategory(fiber) {
    return fiber.hasPvReussite === true || fiber.hasDcoPvReussite === true;
  }

  /**
   * Vérifier si une fibre est dans la catégorie Échec
   * @param {Object} fiber - Objet fibre
   * @returns {boolean} true si la fibre est en catégorie Échec uniquement
   */
  static _isEchecCategory(fiber) {
    return fiber.hasPvEchec === true || fiber.hasDcoPvEchec === true;
  }

  /**
   * Vérifier si une fibre est dans la catégorie Hors Service
   * @param {Object} fiber - Objet fibre
   * @returns {boolean} true si la fibre est en catégorie Hors Service uniquement
   */
  static _isHsCategory(fiber) {
    return fiber.hasPvHs === true;
  }

  /**
   * Ajouter une erreur au résultat d'une fibre
   */
  static _addError(fiber, rule, title, message) {
    // Initialize testResults if it doesn't exist
    if (!fiber.testResults) {
      fiber.testResults = {
        status: "success",
        hasError: false,
        messages: [],
      };
    }

    // Configure error details
    fiber.testResults.status = "error";
    fiber.testResults.hasError = true;
    fiber.testResults.messages.push({
      type: "error",
      rule,
      title,
      message,
    });
  }
}
